import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft2, ArrowRight2, CloseCircle } from 'iconsax-react';
import TitleText from './TitleText';
import { cn } from '@/lib/helperClass';
import { useDispatch } from 'react-redux';
import { setModalId } from '@/store/slices/publicSlice';

export interface DrawerHeaderProps {
  title: React.ReactNode;
  onClose?: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  prevDisabled?: boolean;
  nextDisalbed?: boolean;
  nextDisabled?: boolean;
  extra?: React.ReactNode;
  className?: string;
  'data-test'?: string;
}

export default function DrawerHeader({
  title,
  onClose,
  onNext,
  onPrev,
  prevDisabled = false,
  nextDisalbed,
  nextDisabled,
  extra,
  className,
  'data-test': dataTest = 'drawer-header',
}: DrawerHeaderProps) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const isRtl = i18n.language === 'ar';

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      dispatch(setModalId(null));
    }
  };

  const isNextDisabled = nextDisabled ?? nextDisalbed ?? false;
  const showNav = onPrev !== undefined || onNext !== undefined;

  return (
    <div
      className={cn(
        'drawer-header flex items-center justify-between w-full gap-3 pt-1 pb-1',
        className
      )}
      data-test={dataTest}
    >
      <div className="flex items-center gap-3 flex-wrap min-w-0">
        {!!title && (
          <TitleText
            text={title as string}
            className="!font-bold !text-2xl !leading-6 text-gray-900 dark:text-gray-100 truncate"
          />
        )}

        {showNav && (
          <div className="flex items-center gap-2 shrink-0">
            {onPrev && (
              <button
                type="button"
                className="text-text-secondary dark:text-gray-400 text-base flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent rounded-lg px-6 py-3 font-medium transition-colors cursor-pointer"
                onClick={onPrev}
                disabled={prevDisabled}
                data-test="drawer-header-prev-btn"
              >
                {isRtl ? <ArrowRight2 size={20} /> : <ArrowLeft2 size={20} />}
                <span>{t('home.prev', 'Previous')}</span>
              </button>
            )}

            {onNext && (
              <button
                type="button"
                className="text-text-secondary dark:text-gray-400 text-base flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent rounded-lg px-6 py-3 font-medium transition-colors cursor-pointer"
                onClick={onNext}
                disabled={isNextDisabled}
                data-test="drawer-header-next-btn"
              >
                <span>{t('home.next', 'Next')}</span>
                {isRtl ? <ArrowLeft2 size={20} /> : <ArrowRight2 size={20} />}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {extra}

        <button
          type="button"
          onClick={handleClose}
          className="p-3 min-w-0 h-auto text-gray-400 rounded-full bg-soft-background hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-200 dark:hover:bg-gray-800  transition-colors flex items-center justify-center cursor-pointer"
          data-test="drawer-header-close-btn"
          data-testid="drawer-header-close-btn"
          aria-label="Close"
        >
          <CloseCircle
            size={22}
            variant="Bold"
            className="text-light-text-primary dark:text-dark-text-primary hover:opacity-80 transition-opacity"
          />
        </button>
      </div>
    </div>
  );
}
